import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Text } from '@theme-ui/components';
import { useMutation, useQuery } from '@apollo/client';
import { css } from '@emotion/react';

import { AlertTitle, AlertType } from '@app/components/Alert';
import { Button } from '@app/components/Button';
import useCartAlerts from '@app/hooks/useCartAlerts';

import {
  ButtonCell,
  CartGridCell,
  CouponContentCell,
  Input,
  InputCell,
  RemoveButton,
  ShowFormButton,
} from '../Cart.styles';
import type { AppliedGiftCardsQuery } from './__generated__/AppliedGiftCardsQuery';
import type {
  ApplyGiftCardMutation,
  ApplyGiftCardMutationVariables,
} from './__generated__/ApplyGiftCardMutation';
import type {
  RemoveGiftCardMutation,
  RemoveGiftCardMutationVariables,
} from './__generated__/RemoveGiftCardMutation';
import messages from './CartGiftCard.messages';
import {
  APPLIED_GIFT_CARDS_QUERY,
  APPLY_GIFT_CARD_MUTATION,
  REMOVE_GIFT_CARD_MUTATION,
} from './CartGiftCard.queries';

export const AppliedGiftCards = ({ cartId }: { cartId?: string }) => {
  const { data, loading } = useQuery<AppliedGiftCardsQuery>(
    APPLIED_GIFT_CARDS_QUERY,
    {
      variables: { cartId },
      skip: !cartId,
    },
  );
  const [removeCoupon] = useMutation<
    RemoveGiftCardMutation,
    RemoveGiftCardMutationVariables
  >(REMOVE_GIFT_CARD_MUTATION);

  const coupons = data?.magento2?.cart?.appliedGiftCards;

  if (loading || !coupons || !coupons.length || !cartId) return null;

  return (
    <>
      {coupons.map((coupon) => {
        return coupon ? (
          <GiftCard
            onRemove={(code) => {
              removeCoupon({ variables: { code, cartId } });
            }}
            key={coupon.code}
            code={coupon.code ?? ''}
          />
        ) : null;
      })}
    </>
  );
};

const GiftCard = ({
  code,
  onRemove,
}: {
  code: string;
  onRemove: (code: string) => void;
}) => {
  return (
    <>
      <CartGridCell hAlign="center" />
      <CouponContentCell data-testid="gift-card-applied">
        <Text
          sx={{ fontWeight: 500 }}
          mb={9}
          data-testid={`gift-card-applied-${code}-label`}
        >
          <FormattedMessage {...messages.giftCard} /> ({code})
        </Text>
        <RemoveButton
          type="transparent"
          data-testid="gift-card-applied-remove"
          onClick={() => onRemove(code)}
        >
          <FormattedMessage {...messages.removeCoupon} />
        </RemoveButton>
      </CouponContentCell>
    </>
  );
};

export const GiftCardForm = ({ cartId }: { cartId?: string }) => {
  const { clearCartAlerts, addCartAlerts } = useCartAlerts();
  const [applyCoupon, { loading }] = useMutation<
    ApplyGiftCardMutation,
    ApplyGiftCardMutationVariables
  >(APPLY_GIFT_CARD_MUTATION, {
    onError: (error) => {
      addCartAlerts([
        {
          type: AlertType.Error,
          content: <AlertTitle>{error.message}</AlertTitle>,
          shouldShake: true,
        },
      ]);
    },
    onCompleted: () => {
      addCartAlerts([
        {
          type: AlertType.Success,
          content: (
            <AlertTitle>
              <FormattedMessage {...messages.giftCardUpdated} />
            </AlertTitle>
          ),
        },
      ]);
    },
  });
  const [coupon, setCoupon] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!coupon || !cartId) return;

    clearCartAlerts();

    await applyCoupon({
      variables: {
        cartId,
        code: coupon,
      },
    });
    setCoupon('');
  }, [applyCoupon, clearCartAlerts, coupon, cartId]);

  if (isVisible) {
    return (
      <>
        <CartGridCell hAlign="center" />
        <CartGridCell>
          <Text sx={{ fontWeight: 500 }}>
            <FormattedMessage {...messages.giftCard} />:
          </Text>
        </CartGridCell>
        <InputCell>
          <Input
            data-testid="gift-card-form-input"
            type="text"
            value={coupon}
            disabled={loading}
            onChange={(e) => setCoupon(e.target.value)}
          />
        </InputCell>
        <ButtonCell>
          <Button
            data-testid="gift-card-form-submit"
            type="primary"
            disabled={loading || !coupon}
            onClick={handleSubmit}
          >
            <FormattedMessage {...messages.applyCoupon} />
          </Button>
        </ButtonCell>
      </>
    );
  }

  return (
    <>
      <CartGridCell hAlign="center" />
      <CartGridCell
        css={css`
          grid-column: 2/6;
        `}
      >
        <ShowFormButton
          type="transparent"
          onClick={() => setIsVisible(true)}
          data-testid="show-gift-card-form-button"
        >
          + <FormattedMessage {...messages.showGiftCardForm} />
        </ShowFormButton>
      </CartGridCell>
    </>
  );
};
